import { ArrowRight, Sparkles } from "lucide-react";
import logoGreen from "/src/assets/images/logo-green.png";
import whatsappIcon from "/src/assets/icons/whatsapp.svg";

/**
 * Seção Hero - Chamada principal da landing page
 * Apresenta a proposta de valor e botão de CTA com efeitos de luz
 */
export function Hero() {
    return (
        <section className="relative h-screen flex items-center bg-slate-950 overflow-hidden">
            {/* Background grid estilo Scott */}
            <div className="absolute inset-0 bg-grid opacity-50" />

            {/* Elementos decorativos de fundo com glow */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-500/30 rounded-full blur-[100px] animate-pulse" />
                <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-brand-600/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-brand-400/10 rounded-full blur-[150px]" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                {/* Logo Verde Centralizado */}
                <div className="flex justify-center mb-6 lg:mb-8">
                    <img src={logoGreen} alt="Agência Gens" className="h-12 md:h-14 w-auto drop-shadow-[0_0_25px_rgba(185,255,102,0.6)]" />
                </div>

                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Coluna de texto */}
                    <div className="text-center lg:text-left">
                        {/* Badge de destaque com neon */}
                        <div className="inline-flex items-center gap-2 bg-brand-500/10 border border-brand-400/50 rounded-full px-3 py-1.5 mb-4 shadow-[0_0_20px_rgba(34,197,94,0.3)]">
                            <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                            <span className="text-brand-300 text-xs sm:text-sm font-medium">
                                Método exclusivo para clínicas
                            </span>
                        </div>

                        {/* Título principal */}
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                            Impulsione sua clínica com{" "}
                            <span className="text-brand-400 drop-shadow-[0_0_15px_rgba(74,222,128,0.5)]">
                                inteligência artificial
                            </span>{" "}
                            e tráfego pago especializado
                        </h1>

                        {/* Subtítulo */}
                        <p className="text-base sm:text-lg text-slate-300 mb-6 max-w-2xl mx-auto lg:mx-0">
                            Transformamos clínicas em máquinas de agendamento. Combinamos
                            tráfego pago estratégico, atendimento com IA e treinamento
                            comercial para você nunca mais se preocupar com a agenda vazia.
                        </p>

                        {/* Botão CTA principal com efeito de luz - Link direto para WhatsApp */}
                        <a
                            href="https://api.whatsapp.com/send/?phone=5583999004689&text=Olá%2C+gostaria+de+informações+sobre+o+tráfego+pago+e+inteligência+artificial&type=phone_number&app_absent=0"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-brand-400 hover:bg-brand-500 text-slate-950 text-base sm:text-lg font-bold px-6 py-3 rounded-full transition-all duration-300 transform hover:scale-105 btn-glow animate-pulse-glow shadow-[0_0_30px_rgba(185,255,102,0.4)]"
                        >
                            <img src={whatsappIcon} alt="" className="w-5 h-5 mr-1" />
                            Quero minha vaga agora
                            <ArrowRight className="w-4 h-4" />
                        </a>

                        {/* Indicadores de confiança */}
                        <div className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-slate-400 text-xs sm:text-sm">
                            <div className="flex items-center gap-2">
                                <div className="flex -space-x-2">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div
                                            key={i}
                                            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-brand-700 border-2 border-slate-900 flex items-center justify-center text-[10px] sm:text-xs font-bold text-white"
                                        >
                                            {i}
                                        </div>
                                    ))}
                                </div>
                                <span>+150 clínicas atendidas</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                <span>4.9/5 de satisfação</span>
                            </div>
                        </div>
                    </div>

                    {/* Coluna visual com cards neon */}
                    <div className="hidden lg:block">
                        <div className="relative">
                            {/* Card principal com estatísticas */}
                            <div className="bg-slate-900/80 backdrop-blur-lg rounded-2xl p-6 border border-brand-500/30 card-neon">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-brand-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.5)] mb-1">
                                            +500%
                                        </div>
                                        <div className="text-slate-400 text-xs">
                                            Aumento médio em agendamentos
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-brand-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.5)] mb-1">
                                            3x
                                        </div>
                                        <div className="text-slate-400 text-xs">
                                            Retorno sobre investimento
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-brand-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.5)] mb-1">
                                            24/7
                                        </div>
                                        <div className="text-slate-400 text-xs">
                                            Atendimento com IA
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-brand-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.5)] mb-1">
                                            98%
                                        </div>
                                        <div className="text-slate-400 text-xs">
                                            Taxa de ocupação
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Elementos flutuantes decorativos com glow */}
                            <div className="absolute -top-4 -right-4 bg-brand-600 text-white px-3 py-1.5 rounded-lg shadow-[0_0_30px_rgba(34,197,94,0.5)] transform rotate-6 border border-brand-400">
                                <span className="font-bold text-sm">🚀 Resultados em 30 dias</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
